import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { format } from 'date-fns';
import CustomDatePicker from './CustomDatePicker';
import Icon from './Icon';
import '../css/sort-menu.css';

const LogsSortFiltersMenu = ({
  sort,
  direction,
  onSortChange,
  onDirectionChange,
  createdFrom,
  createdTo,
  targetId,
  actorId,
  onDateFromChange,
  onDateToChange,
  onTargetIdChange,
  onActorIdChange,
  translationKey = 'home.logs',
  hideExtraFiltersToggle = false,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showExtraFilters, setShowExtraFilters] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const sortOptions = [
    { value: 'createdAt', label: t(`${translationKey}.filters.sortByCreatedDate`) }
  ];

  const directionOptions = [
    { value: 'DESC', label: t(`orders.filters.descending`), icon: '↓' },
    { value: 'ASC', label: t(`orders.filters.ascending`), icon: '↑' }
  ];

  const handleSortSelect = (value) => {
    if (onSortChange) onSortChange(value);
  };

  const handleDirectionSelect = (value) => {
    if (onDirectionChange) onDirectionChange(value);
  };

  const isSortMenuActive = useMemo(() => {
    const dirNonDefault = direction !== 'DESC';
    const extra =
      Boolean(createdFrom) ||
      Boolean(createdTo) ||
      Boolean(targetId && String(targetId).trim()) ||
      Boolean(actorId && String(actorId).trim());
    return dirNonDefault || extra;
  }, [direction, createdFrom, createdTo, targetId, actorId]);

  return (
    <div className="sort-menu-wrapper orders-sort-filters-wrapper" ref={menuRef}>
      <button
        type="button"
        className={`sort-menu-button${isSortMenuActive ? ' filter-active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t(`orders.filters.sortButton`)}
      >
        <span className="sort-menu-icon">
          <Icon name="sort-arrows" size={14} />
        </span>
        <span className="sort-menu-label">{t(`orders.filters.sortButton`)}</span>
        <span className="sort-menu-arrow">
          <Icon
            name="chevron-down"
            size={12}
            className={`expand-icon ${isOpen ? 'open' : ''}`}
          />
        </span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="sort-menu-dropdown orders-sort-filters-dropdown"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
          >
            <div className="orders-sort-main-grid">
              <div className="sort-menu-section">
                <div className="sort-menu-section-title">{t(`orders.filters.sortBy`)}</div>
                <div className="sort-menu-options">
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`sort-menu-option ${sort === option.value ? 'active' : ''}`}
                      onClick={() => handleSortSelect(option.value)}
                    >
                      <span className="sort-menu-option-label">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="sort-menu-section">
                <div className="sort-menu-section-title">{t(`orders.filters.sortDirection`)}</div>
                <div className="sort-menu-options">
                  {directionOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`sort-menu-option ${direction === option.value ? 'active' : ''}`}
                      onClick={() => handleDirectionSelect(option.value)}
                    >
                      <span className="sort-menu-option-icon">{option.icon}</span>
                      <span className="sort-menu-option-label">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="extra-filters-accordion">
              <button
                type="button"
                className="orders-extra-filters-toggle"
                onClick={() => setShowExtraFilters(!showExtraFilters)}
                aria-expanded={showExtraFilters}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon name="sliders" size={15} />
                  {t('orders.filters.extraFilters')}
                </span>
                <span className="orders-extra-filters-toggle-icon">
                  <Icon name="chevron-down" size={16} />
                </span>
              </button>

              <div className={`orders-extra-filters-body ${showExtraFilters ? 'open' : ''}`}>
                <div>
                  <div className="more-filters-content" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', padding: '16px' }}>
                    <div className="more-filters-item">
                      <label className="more-filters-label">
                        {t(`${translationKey}.filters.targetId`)}
                      </label>
                      <input
                        type="text"
                        value={targetId || ''}
                        onChange={(e) => onTargetIdChange(e.target.value)}
                        placeholder={t(`${translationKey}.filters.targetIdPlaceholder`)}
                        className="more-filters-input"
                        style={{ backgroundImage: 'none', paddingRight: '14px' }}
                      />
                    </div>

                    <div className="more-filters-item">
                      <label className="more-filters-label">
                        {t(`${translationKey}.filters.actorId`)}
                      </label>
                      <input
                        type="text"
                        value={actorId || ''}
                        onChange={(e) => onActorIdChange(e.target.value)}
                        placeholder={t(`${translationKey}.filters.actorIdPlaceholder`)}
                        className="more-filters-input"
                        style={{ backgroundImage: 'none', paddingRight: '14px' }}
                      />
                    </div>

                    <div className="more-filters-item">
                      <label className="more-filters-label">
                        {t(`${translationKey}.filters.from`)}
                      </label>
                      <CustomDatePicker
                        selected={createdFrom ? new Date(createdFrom) : null}
                        onChange={(date) =>
                          onDateFromChange(
                            date ? format(date, "yyyy-MM-dd'T'HH:mm") : ''
                          )
                        }
                      />
                    </div>

                    <div className="more-filters-item">
                      <label className="more-filters-label">
                        {t(`${translationKey}.filters.to`)}
                      </label>
                      <CustomDatePicker
                        selected={createdTo ? new Date(createdTo) : null}
                        onChange={(date) =>
                          onDateToChange(
                            date ? format(date, "yyyy-MM-dd'T'HH:mm") : ''
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LogsSortFiltersMenu;
